import { lazy } from 'react'
import { Navigate, createBrowserRouter } from 'react-router'
import { RootLayout } from '@/app/RootLayout'
import { LazyRoute } from '@/app/LazyRoute'
import { DocumentRoute } from '@/app/DocumentRoute'
import { SpecRoute } from '@/app/SpecRoute'
import { routeLoader } from '@/app/routePreload'
import { NotFound } from '@/routes/NotFound'
import { PortedPage } from '@/routes/PortedPage'
import { OutboundStub } from '@/routes/OutboundStub'
import { PORTED_ROUTES } from '@/pages'
import { PAGE_META } from '@/content/pages/pages.generated'
import { LEGAL_ROUTES as LEGAL_ROUTE_DEFS } from '@/routes/routes'

/*
 * The document templates, loaded on demand.
 *
 * 40 of the 59 routes render through these four: 17 posts, 23 support docs and
 * the two index pages. Lazy because a visitor landing on a marketing page has
 * no use for MDX plumbing, and each document is already its own chunk.
 */
const PostPage = lazy(() => import('@/routes/PostPage').then((m) => ({ default: m.PostPage })))
const SupportDocPage = lazy(() =>
  import('@/routes/SupportDocPage').then((m) => ({ default: m.SupportDocPage })),
)
const BlogIndex = lazy(() => import('@/routes/BlogIndex').then((m) => ({ default: m.BlogIndex })))
const SupportIndex = lazy(() =>
  import('@/routes/SupportIndex').then((m) => ({ default: m.SupportIndex })),
)

// Internal tooling, loaded on demand.
const Styleguide = lazy(() =>
  import('@/routes/Styleguide').then((m) => ({ default: m.Styleguide })),
)
const DocPreview = lazy(() =>
  import('@/routes/DocPreview').then((m) => ({ default: m.DocPreview })),
)
// Renders a spec by slug even while its route still serves ported markup —
// the only way to see a shadowed spec. See SpecPreview and check-migration.
const SpecPreview = lazy(() =>
  import('@/routes/SpecPreview').then((m) => ({ default: m.SpecPreview })),
)
/*
 * `/terms` and `/privacy` — long-form documents, not marketing specs.
 * `routes.ts` has said `template: 'legal'` since the port; this implements it.
 */
const LegalPage = lazy(() => import('@/routes/LegalPage').then((m) => ({ default: m.LegalPage })))

/*
 * `/terms` and `/privacy`, once each has stopped being ported.
 *
 * Same flip as everything else: write the document, delete the route's line
 * from `src/pages/index.ts`, and it lands here. `/privacy` is still ported and
 * so is still excluded — it needs its MDX written first.
 */
const LEGAL_ROUTES = LEGAL_ROUTE_DEFS.map((r) => r.path).filter((r) => !PORTED_ROUTES.includes(r))

/*
 * Pages with a generated spec but no ported markup — the routes recovered from
 * the live site that the capture never held. They render from the design
 * system; everything in PORTED_ROUTES keeps its Webflow markup.
 *
 * Legal routes are excluded: they have a spec entry for its metadata, but the
 * document itself renders through `LegalPage`, not `PageRenderer`.
 */
const SPEC_ONLY_ROUTES = PAGE_META.map((m) => m.route).filter(
  (r) => !PORTED_ROUTES.includes(r) && !LEGAL_ROUTES.includes(r),
)

/*
 * Two kinds of route.
 *
 * The 40 documents and both index pages render from the design system: typed
 * content in `src/content` through four templates. The remaining 17 marketing
 * pages still render their ported markup, from `src/pages/index.ts`, which the
 * porter generates from the capture — so there is no second list to drift.
 *
 * The porter skips the retired routes outright (`RETIRED_ROUTES` in
 * port-pages.ts), so `PORTED_ROUTES` and the templates below cannot both claim
 * a path.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <NotFound />,
    children: [
      {
        path: 'styleguide',
        element: (
          <LazyRoute>
            <Styleguide />
          </LazyRoute>
        ),
      },
      {
        path: 'content-preview',
        element: (
          <LazyRoute>
            <DocPreview />
          </LazyRoute>
        ),
      },
      {
        path: 'spec-preview/:slug',
        element: (
          <LazyRoute>
            <SpecPreview />
          </LazyRoute>
        ),
      },

      // No source: the capture recorded Webflow's 404 for this URL.
      { path: 'outbound', element: <OutboundStub /> },

      /*
       * Webflow served /search server-side; a static build has no equivalent.
       * The URL is kept alive and pointed at the support index rather than
       * 404ing anyone who follows an old link. `build-seo` marks it noindex.
       */
      { path: 'search', element: <Navigate to="/support" replace /> },

      /* ── The design system's routes ─────────────────────────────── */
      {
        path: 'blog',
        element: (
          <DocumentRoute>
            <BlogIndex />
          </DocumentRoute>
        ),
      },
      {
        path: 'post/:slug',
        element: (
          <DocumentRoute>
            <PostPage />
          </DocumentRoute>
        ),
      },
      {
        path: 'support',
        element: (
          <DocumentRoute>
            <SupportIndex />
          </DocumentRoute>
        ),
      },
      {
        path: 'support/:group/:slug',
        element: (
          <DocumentRoute>
            <SupportDocPage />
          </DocumentRoute>
        ),
      },

      ...LEGAL_ROUTES.map((route) => ({
        path: route.slice(1),
        element: (
          <LazyRoute>
            <LegalPage />
          </LazyRoute>
        ),
      })),

      /*
       * Both kinds of marketing route wait for their code in a loader, so the
       * router holds the current page on screen until the next one can render
       * whole. See routePreload.
       */
      ...SPEC_ONLY_ROUTES.map((route) => ({
        path: route.slice(1),
        loader: routeLoader,
        element: (
          <LazyRoute>
            <SpecRoute />
          </LazyRoute>
        ),
      })),

      ...PORTED_ROUTES.map((route) => ({
        index: route === '/',
        path: route === '/' ? undefined : route.slice(1),
        loader: routeLoader,
        element: <PortedPage />,
      })),

      { path: '*', element: <NotFound /> },
    ],
  },
])
